import { useState, useEffect, useMemo } from "react";
import axios from "axios";
import { API, useAuth, useSelectedDate, getWeeksInMonth } from "../App";
import { useNavigate } from "react-router-dom";
import { Search, ChevronRight, UserPlus, Building2, Users, TrendingUp, CalendarDays } from "lucide-react";
import { Button } from "../components/ui/button";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
  DialogFooter,
} from "../components/ui/dialog";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "../components/ui/select";

const DEPARTMENTS = {
  all: { label: "Teams", color: "bg-slate-600", textColor: "text-slate-400" },
  sales: { label: "Sales", color: "bg-indigo-600", textColor: "text-indigo-400" },
  csm: { label: "Customer Success", color: "bg-emerald-600", textColor: "text-emerald-400" },
  dev: { label: "Development", color: "bg-purple-600", textColor: "text-purple-400" },
  support: { label: "Support", color: "bg-amber-600", textColor: "text-amber-400" },
  marketing: { label: "Marketing", color: "bg-pink-600", textColor: "text-pink-400" },
};

const MONTHS = [
  { value: 1, label: "January" },
  { value: 2, label: "February" },
  { value: 3, label: "March" },
  { value: 4, label: "April" },
  { value: 5, label: "May" },
  { value: 6, label: "June" },
  { value: 7, label: "July" },
  { value: 8, label: "August" },
  { value: 9, label: "September" },
  { value: 10, label: "October" },
  { value: 11, label: "November" },
  { value: 12, label: "December" },
];

// Generate year options (current year and previous 2 years)
const currentYearConst = new Date().getFullYear();
const YEARS = [currentYearConst, currentYearConst - 1, currentYearConst - 2];

const Scorecards = () => {
  const { token, isAdmin } = useAuth();
  const { selectedMonth, setSelectedMonth, selectedYear, setSelectedYear, viewMode, setViewMode, selectedWeek, setSelectedWeek } = useSelectedDate();
  const [salespeople, setSalespeople] = useState([]);
  const [teamStats, setTeamStats] = useState(null);
  const [loading, setLoading] = useState(true);
  const [searchTerm, setSearchTerm] = useState("");
  const [selectedDepartment, setSelectedDepartment] = useState("all");
  const [isAddDialogOpen, setIsAddDialogOpen] = useState(false);
  const [newSalesperson, setNewSalesperson] = useState({ name: "", email: "", department: "sales" });
  const navigate = useNavigate();

  const headers = { Authorization: `Bearer ${token}` };
  
  // Get weeks for the selected month
  const weeksInMonth = getWeeksInMonth(selectedYear, selectedMonth);

  useEffect(() => {
    fetchData();
  }, [selectedYear, selectedMonth, viewMode, selectedWeek]);

  const fetchData = async () => {
    setLoading(true);
    try {
      let statsUrl = `${API}/stats/team?year=${selectedYear}&month=${selectedMonth}`;
      if (viewMode === 'weekly' && selectedWeek) {
        statsUrl += `&week=${selectedWeek}`;
      }
      
      const [peopleRes, statsRes] = await Promise.all([
        axios.get(`${API}/salespeople`, { headers }),
        axios.get(statsUrl, { headers })
      ]);
      setSalespeople(peopleRes.data);
      setTeamStats(statsRes.data);
    } catch (error) {
      console.error("Error fetching data:", error);
    } finally {
      setLoading(false);
    }
  };

  const handleAddSalesperson = async () => {
    if (!newSalesperson.name.trim()) return;
    
    try {
      await axios.post(`${API}/salespeople`, newSalesperson, { headers });
      setNewSalesperson({ name: "", email: "", department: "sales" });
      setIsAddDialogOpen(false);
      fetchData();
    } catch (error) {
      console.error("Error adding salesperson:", error);
      alert(error.response?.data?.detail || "Failed to add salesperson. Admin access required.");
    }
  };

  // Create a map of stats by salesperson ID
  const statsMap = useMemo(() => {
    if (!teamStats?.by_salesperson) return {};
    const map = {};
    teamStats.by_salesperson.forEach(s => {
      const total = Object.values(s.totals || {}).reduce((sum, v) => sum + v, 0);
      const deals = s.totals?.new_deals_closed || s.totals?.renewals || s.totals?.features_shipped || 
                    s.totals?.tickets_resolved || s.totals?.leads_generated || 0;
      map[s.salesperson.id] = {
        total,
        deals,
        weeks: s.entry_count || 0
      };
    });
    return map;
  }, [teamStats]);

  // Filter by search term and department
  const filteredSalespeople = salespeople.filter((sp) => {
    const matchesSearch = sp.name.toLowerCase().includes(searchTerm.toLowerCase());
    const matchesDept = selectedDepartment === "all" || sp.department === selectedDepartment;
    return matchesSearch && matchesDept;
  });

  // Sort by total points descending
  const sortedSalespeople = [...filteredSalespeople].sort((a, b) => {
    const aTotal = statsMap[a.id]?.total || 0;
    const bTotal = statsMap[b.id]?.total || 0;
    return bTotal - aTotal;
  });

  // Group by department for display
  const groupedByDept = sortedSalespeople.reduce((acc, sp) => {
    const dept = sp.department || "sales";
    if (!acc[dept]) acc[dept] = [];
    acc[dept].push(sp);
    return acc;
  }, {});

  // Count per department
  const deptCounts = salespeople.reduce((acc, sp) => {
    const dept = sp.department || "sales";
    acc[dept] = (acc[dept] || 0) + 1;
    acc.all = (acc.all || 0) + 1;
    return acc;
  }, {});

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="loading-spinner"></div>
      </div>
    );
  }

  return (
    <div className="animate-fade-in" data-testid="scorecards-page">
      {/* Header */}
      <div className="flex flex-wrap items-center justify-between gap-2 mb-4">
        <div>
          <h1 className="text-lg font-bold text-slate-100" style={{ fontFamily: 'Manrope, sans-serif' }}>
            Team Directory
          </h1>
          <p className="text-xs text-slate-400">
            {MONTHS.find(m => m.value === selectedMonth)?.label} {selectedYear} • {filteredSalespeople.length} members
          </p>
        </div>
        
        {isAdmin && (
          <Dialog open={isAddDialogOpen} onOpenChange={setIsAddDialogOpen}>
            <DialogTrigger asChild>
              <Button 
                className="h-8 text-xs bg-indigo-600 hover:bg-indigo-700" 
                data-testid="add-salesperson-btn"
              >
                <UserPlus className="mr-1 h-3 w-3" />
                Add
              </Button>
            </DialogTrigger>
            <DialogContent className="bg-slate-800 border-slate-700" data-testid="add-salesperson-dialog">
              <DialogHeader>
                <DialogTitle className="text-slate-100">Add New Team Member</DialogTitle>
                <DialogDescription className="text-slate-400">
                  Add a new team member to track their performance.
                </DialogDescription>
              </DialogHeader>
              <div className="grid gap-4 py-4">
                <div className="grid gap-2">
                  <Label htmlFor="name" className="text-slate-300">Name</Label>
                  <Input
                    id="name"
                    placeholder="Enter name"
                    value={newSalesperson.name}
                    onChange={(e) => setNewSalesperson({ ...newSalesperson, name: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="salesperson-name-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label htmlFor="email" className="text-slate-300">Email (optional)</Label>
                  <Input
                    id="email"
                    type="email"
                    placeholder="Enter email"
                    value={newSalesperson.email}
                    onChange={(e) => setNewSalesperson({ ...newSalesperson, email: e.target.value })}
                    className="bg-slate-900 border-slate-700 text-white"
                    data-testid="salesperson-email-input"
                  />
                </div>
                <div className="grid gap-2">
                  <Label className="text-slate-300">Department</Label>
                  <Select 
                    value={newSalesperson.department} 
                    onValueChange={(v) => setNewSalesperson({ ...newSalesperson, department: v })}
                  >
                    <SelectTrigger className="bg-slate-900 border-slate-700 text-white">
                      <SelectValue />
                    </SelectTrigger>
                    <SelectContent className="bg-slate-800 border-slate-700">
                      {Object.entries(DEPARTMENTS).filter(([k]) => k !== "all").map(([key, dept]) => (
                        <SelectItem key={key} value={key} className="text-slate-200">
                          {dept.label}
                        </SelectItem>
                      ))}
                    </SelectContent>
                  </Select>
                </div>
              </div>
              <DialogFooter>
              <Button variant="outline" onClick={() => setIsAddDialogOpen(false)} className="border-slate-600 text-slate-300">
                Cancel
              </Button>
              <Button 
                onClick={handleAddSalesperson}
                className="bg-indigo-600 hover:bg-indigo-700"
                data-testid="save-salesperson-btn"
              >
                Add Member
              </Button>
            </DialogFooter>
          </DialogContent>
        </Dialog>
        )}
      </div>

      {/* Filters Row */}
      <div className="flex flex-wrap items-center gap-2 mb-4">
        {/* View Mode Toggle */}
        <div className="flex rounded-lg overflow-hidden border border-slate-700 h-8">
          <button
            onClick={() => setViewMode('weekly')}
            className={`px-3 text-xs font-medium transition-colors ${
              viewMode === 'weekly' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            Weekly
          </button>
          <button
            onClick={() => setViewMode('monthly')}
            className={`px-3 text-xs font-medium transition-colors ${
              viewMode === 'monthly' 
                ? 'bg-indigo-600 text-white' 
                : 'bg-slate-800 text-slate-400 hover:text-slate-200'
            }`}
          >
            Monthly
          </button>
        </div>

        {/* Month/Year Selectors */}
        <Select value={String(selectedMonth)} onValueChange={(v) => setSelectedMonth(Number(v))}>
          <SelectTrigger className="w-[110px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="month-selector">
            <SelectValue placeholder="Month" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {MONTHS.map((month) => (
              <SelectItem key={month.value} value={String(month.value)} className="text-slate-200 text-xs">
                {month.label}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <Select value={String(selectedYear)} onValueChange={(v) => setSelectedYear(Number(v))}>
          <SelectTrigger className="w-[80px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="year-selector">
            <SelectValue placeholder="Year" />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {YEARS.map((year) => (
              <SelectItem key={year} value={String(year)} className="text-slate-200 text-xs">
                {year}
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        {/* Week Selector - only shown in weekly mode */}
        {viewMode === 'weekly' && weeksInMonth.length > 0 && (
          <Select value={selectedWeek} onValueChange={setSelectedWeek}>
            <SelectTrigger className="w-[160px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200" data-testid="week-selector">
              <CalendarDays size={12} className="mr-1" />
              <SelectValue placeholder="Select week" />
            </SelectTrigger>
            <SelectContent className="bg-slate-800 border-slate-700">
              {weeksInMonth.map((week) => (
                <SelectItem key={week.value} value={week.value} className="text-slate-200 text-xs">
                  {week.label}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
        )}

        <Select value={selectedDepartment} onValueChange={setSelectedDepartment}>
          <SelectTrigger className="w-[140px] h-8 text-xs bg-slate-800 border-slate-700 text-slate-200">
            <Building2 size={12} className="mr-1" />
            <SelectValue />
          </SelectTrigger>
          <SelectContent className="bg-slate-800 border-slate-700">
            {Object.entries(DEPARTMENTS).map(([key, dept]) => (
              <SelectItem key={key} value={key} className="text-slate-200 text-xs">
                {dept.label} ({deptCounts[key] || 0})
              </SelectItem>
            ))}
          </SelectContent>
        </Select>

        <div className="relative flex-1 min-w-[150px] max-w-[250px]">
          <input
            type="text"
            placeholder="Search..."
            className="h-8 w-full pl-8 pr-3 text-xs bg-slate-800 border border-slate-700 rounded-lg text-white placeholder-slate-500 focus:outline-none focus:border-slate-600"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            data-testid="search-input"
          />
          <Search className="absolute left-2.5 top-1/2 transform -translate-y-1/2 text-slate-500" size={14} />
        </div>
      </div>

      {/* Team Members */}
      {selectedDepartment === "all" ? (
        Object.entries(DEPARTMENTS).filter(([k]) => k !== "all" && groupedByDept[k]?.length > 0).map(([deptKey, deptConfig]) => (
          <div key={deptKey} className="mb-5">
            <div className="flex items-center gap-2 mb-2">
              <span className={`w-2 h-2 rounded-full ${deptConfig.color}`}></span>
              <h3 className={`text-xs font-semibold ${deptConfig.textColor} uppercase tracking-wide`}>
                {deptConfig.label}
              </h3>
              <span className="text-[10px] text-slate-500 bg-slate-800 px-1.5 py-0.5 rounded">
                {groupedByDept[deptKey]?.length || 0}
              </span>
            </div>
            <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
              {groupedByDept[deptKey]?.map((person, index) => (
                <PersonRow 
                  key={person.id} 
                  person={person} 
                  index={index} 
                  deptConfig={deptConfig}
                  stats={statsMap[person.id]}
                  onClick={() => navigate(`/scorecards/${person.id}`)}
                  isLast={index === groupedByDept[deptKey].length - 1}
                />
              ))}
            </div>
          </div>
        ))
      ) : (
        <div className="bg-slate-800/30 rounded-lg border border-slate-700/50 overflow-hidden">
          {sortedSalespeople.map((person, index) => {
            const dept = person.department || "sales";
            const deptConfig = DEPARTMENTS[dept] || DEPARTMENTS.sales;
            return (
              <PersonRow 
                key={person.id} 
                person={person} 
                index={index} 
                deptConfig={deptConfig}
                stats={statsMap[person.id]}
                onClick={() => navigate(`/scorecards/${person.id}`)}
                isLast={index === sortedSalespeople.length - 1}
              />
            );
          })}
        </div>
      )}

      {filteredSalespeople.length === 0 && (
        <div className="text-center py-12 bg-slate-800/30 rounded-lg border border-slate-700/50">
          <Users className="w-10 h-10 mx-auto text-slate-600 mb-2" />
          <h3 className="text-sm font-medium text-slate-400 mb-1">No team members found</h3>
          <p className="text-xs text-slate-500">
            {searchTerm ? "Try a different search term" : "Add your first team member to get started"}
          </p>
        </div>
      )}
    </div>
  );
};

// Person row with stats
const PersonRow = ({ person, index, deptConfig, stats, onClick, isLast }) => {
  const total = stats?.total || 0;
  const deals = stats?.deals || 0;
  const weeks = stats?.weeks || 0;

  return (
    <div
      className={`flex items-center gap-3 px-3 py-2.5 cursor-pointer hover:bg-slate-700/30 transition-all group ${
        !isLast ? 'border-b border-slate-700/30' : ''
      }`}
      onClick={onClick}
      data-testid={`scorecard-card-${index}`}
    >
      {/* Initial */}
      <div className={`w-9 h-9 ${deptConfig.color} rounded-lg flex items-center justify-center text-white font-semibold text-sm flex-shrink-0`}>
        {person.name.charAt(0)}
      </div>
      
      {/* Name */}
      <div className="flex-1 min-w-0">
        <h3 className="font-medium text-slate-100 text-sm truncate group-hover:text-white transition-colors">
          {person.name}
        </h3>
      </div>
      
      {/* Stats */}
      <div className="flex items-center gap-3 flex-shrink-0">
        {total > 0 ? (
          <>
            <div className="text-right hidden sm:block">
              <div className="text-xs text-slate-500">{weeks}w</div>
            </div>
            <div className="text-right">
              <div className="text-sm font-bold text-emerald-400">{deals}</div>
              <div className="text-[10px] text-slate-500">deals</div>
            </div>
            <div className="text-right min-w-[60px]">
              <div className="text-sm font-bold text-white">{total.toLocaleString('en-US', { minimumFractionDigits: 0, maximumFractionDigits: 2 })}</div>
              <div className="text-[10px] text-slate-500">pts</div>
            </div>
          </>
        ) : (
          <div className="text-xs text-slate-500 italic">No data</div>
        )}
        <ChevronRight className="text-slate-600 group-hover:text-slate-400 transition-colors" size={16} />
      </div>
    </div>
  );
};

export default Scorecards;
